import type { EntityState } from '../../lib/api.js';
import { useDevices } from '../../context/DevicesContext.js';
import { Toggle } from '../ui/Toggle.js';
import { Slider } from '../ui/Slider.js';
import { friendlyName } from '../../lib/utils.js';
import { useToast } from '../ui/Toast.js';

interface Props {
  entityId: string;
  entity: EntityState;
}

export function FanCard({ entityId, entity }: Props) {
  const { sendCommand, patchEntity } = useDevices();
  const toast = useToast();
  const isOn = entity.state === 'on';
  const pct =
    typeof entity.attributes['percentage'] === 'number'
      ? (entity.attributes['percentage'] as number)
      : 0;

  const handleToggle = async (next: boolean) => {
    const prev = entity;
    patchEntity(entityId, { ...entity, state: next ? 'on' : 'off' });
    try {
      await sendCommand('fan', next ? 'turn_on' : 'turn_off', entityId);
    } catch {
      patchEntity(entityId, prev);
      toast.show('Command failed', 'error');
    }
  };

  const handlePct = async (v: number) => {
    const prev = entity;
    patchEntity(entityId, {
      ...entity,
      state: 'on',
      attributes: { ...entity.attributes, percentage: v },
    });
    try {
      await sendCommand('fan', 'set_percentage', entityId, { percentage: v });
    } catch {
      patchEntity(entityId, prev);
      toast.show('Command failed', 'error');
    }
  };

  return (
    <div
      className="p-4 rounded-xl border flex flex-col gap-3"
      style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-[var(--ac-text)]">
            {friendlyName(entityId, entity.attributes)}
          </div>
          <div className="text-xs text-[var(--ac-text-muted)]">{isOn ? 'On' : 'Off'}</div>
        </div>
        <Toggle checked={isOn} onChange={handleToggle} />
      </div>
      {isOn ? <Slider value={pct} min={0} max={100} onChange={handlePct} label="Speed" /> : null}
    </div>
  );
}
